import type { FilterData } from '@which/shared'
import { getSingleBrand } from '@which/shared'
import { dynamicDatalayerPush } from '@which/shared'

import type {
  FilterItemIntersection,
  NeedsBasedQueryValues,
  ReviewsFilterQueryValues,
} from '../../../../../shared/types/ReviewsFilters'

export const getQueryValues = ({
  data,
  nbfLabel = '',
}: {
  data: FilterData | NeedsBasedQueryValues
  nbfLabel?: string
}) => {
  const singleBrand = getSingleBrand({ data })

  const reviewsFilterQueryValues = Object.entries(data).reduce(
    (queryValues: ReviewsFilterQueryValues, [externalId, filters]) => {
      const additionalQueryValues = Object.keys(filters).reduce(
        (basicFilterQueryValues: { [paramKey: string]: string }, code) => {
          const queryValuesArray = singleBrand ? transformQueryValues(filters[code]) : filters[code]

          if (Array.isArray(filters[code])) {
            return {
              ...basicFilterQueryValues,
              [`filters.${externalId}.${code}`]: [...queryValuesArray].toString(),
            }
          }
          const needsBasedFilterQueryValues = Object.keys(filters?.[code]).reduce(
            (nbfQuery: { [paramKey: string]: string }, identifier) => {
              return {
                ...nbfQuery,
                [`filters.${nbfLabel}.${externalId}.${code}.${identifier}`]: [
                  ...filters[code][identifier],
                ].toString(),
              }
            },
            {}
          )

          return {
            ...basicFilterQueryValues,
            ...needsBasedFilterQueryValues,
          }
        },
        {}
      )

      return {
        ...queryValues,
        ...additionalQueryValues,
      }
    },
    {}
  )

  return reviewsFilterQueryValues
}

const transformQueryValues = (queryValues: string[]) =>
  queryValues.map((item) => replaceUnderscoresWithHyphens(item))

const replaceUnderscoresWithHyphens = (value: string) => value.replace(/(_)/g, '-')

export const updateFilter = ({
  filter = [],
  filterValue,
  removeFilterValue,
  addFilterValue,
}: UpdateFilterParams) => {
  if (Array.isArray(filterValue)) {
    return filterValue
  }

  if (filterValue && addFilterValue) {
    return !filter.includes(filterValue) ? [...filter, filterValue] : filter
  }

  if (filterValue) {
    return filter.includes(filterValue) || removeFilterValue
      ? filter.filter((item) => item !== filterValue)
      : [...filter, filterValue]
  }

  return filter
}

export const pushToDatalayer = ({ categorySlug, filter, segment }) => {
  dynamicDatalayerPush({
    eventCategory: 'Product Listing',
    eventLabel: `${categorySlug} - ${segment} - ${filter}`,
    eventAction: 'Tooltip',
  })
}

export const hasNoApplicableFilters = (filterItems: FilterItemIntersection[]) =>
  filterItems.every((item: FilterItemIntersection) => {
    // v8 ignore else
    if (item.__typename === 'FilterItemWithToFrom') {
      return false
    }

    // v8 ignore else
    if (item.__typename === 'FilterItemWithOptions') {
      return item.options?.every((option) => !option.optionCount)
    }

    return !item.count
  })

///////// IMPLEMENTATION /////////

type UpdateFilterParams = {
  filter?: string[]
  filterValue?: string | string[]
  removeFilterValue?: boolean
  addFilterValue?: boolean
}
